// Les onglets du paramétrage de la Qualité — la LISTE, séparée de l'écran.
//
// Elle vivait dans `Settings.tsx`. La recherche générale doit désormais y
// conduire (retour Ines du 18/08 : « je voudrais que la barre de recherche
// générale cherche également dans les onglets de paramétrage »), et recopier
// la liste dans la palette l'aurait fait diverger au premier onglet ajouté —
// avec le pire symptôme possible : un réglage introuvable à la recherche alors
// qu'il existe, sans que rien ne signale l'écart.
//
// Le RÔLE reste porté par le groupe : la palette applique le même filtre que
// l'écran, si bien qu'un onglet fermé à l'utilisateur reste introuvable ici —
// exactement la règle déjà tenue pour les écrans de la barre latérale.
import type React from 'react';

export type Tab = 'types' | 'disciplines' | 'sites' | 'codification' | 'acces' | 'droitscq'
  | 'utilisateurs' | 'politique' | 'cartouche' | 'office' | 'notif' | 'imapnc'
  | 'imapdemandes' | 'matvalid' | 'emplacements' | 'rhregles' | 'rhferies' | 'rhregimes' | 'quiz'
  | 'cqiregles' | 'annonces' | 'regles' | 'deploiement' | 'actes';

/** Rôle exigé pour VOIR un groupe de réglages.
 *
 *  Le paramétrage n'est plus un bloc « tout ou rien » réservé au RQ : chaque
 *  groupe porte son droit. Sans cela, un gestionnaire RH qui n'est pas RQ
 *  n'aurait pas accès à ses propres règles — et il aurait fallu lui donner le
 *  rôle `rq` en entier, c'est-à-dire tout le circuit documentaire, pour qu'il
 *  puisse saisir des jours fériés. Un technicien, lui, ne porte aucun de ces
 *  rôles : la fenêtre lui reste fermée. */
/* ⚠️ `cqi` n'est PAS un rôle de session : c'est la permission `cqiRegles`,
   portée par le PROFIL (responsable, super-admin) et non par les rôles
   documentaires. Les deux axes existent vraiment et ne se recouvrent pas — un
   responsable de paillasse règle les règles de Westgard sans porter le rôle
   `rq`, qui ouvrirait tout le circuit documentaire. Rabattre ce groupe sur
   `rq` lui aurait retiré un accès qu'il avait déjà, depuis l'écran CQI. */
/* ⚠️ `superadmin` est un PROFIL de compte, pas un rôle documentaire — c'est
   justement ce qui le distingue des trois autres. Il commande la mise à
   disposition des modules : masquer une rubrique la retire à TOUT LE MONDE, y
   compris au responsable qualité. Décider du périmètre de l'outil est donc un
   cran AU-DESSUS du système qualité, et non une de ses attributions.
   Conséquence voulue (RQ, 10/09/2026) : qui porte `rq` sans être super admin
   tient tout le paramétrage qualité et ne peut pas masquer un module. */
export type RoleReglage = 'rq' | 'rh' | 'cqi' | 'superadmin';
export type Section = { group: string; icon: string; role: RoleReglage; items: { tab: Tab; label: string }[] };

export const SECTIONS: Section[] = [
  { group: 'Documentaire', icon: 'doc', role: 'rq', items: [
    { tab: 'types', label: 'Types & circuit' },
    // « Disciplines » tout court : l'intitulé « Disciplines & processus » a
    // longtemps annoncé deux référentiels dans un onglet qui n'en modifiait
    // qu'un. Les processus se règlent au module Processus, où leur pilote est
    // visible — voir le rappel au pied du panneau.
    { tab: 'disciplines', label: 'Disciplines (familles bio)' },
    // Les sites se règlent AVANT la codification, qui leur attribue une
    // abréviation : régler un code pour un site qui n'existe pas encore n'a
    // pas de sens, et le panneau « Code par site » lit cette même liste.
    { tab: 'sites', label: 'Sites du laboratoire' },
    { tab: 'codification', label: 'Codification des références' },
  ] },
  { group: 'Rôles & accès', icon: 'shield', role: 'rq', items: [
    /* LE MODÈLE DES RÔLES (refonte des droits, 16/10/2026) — Personnes, Rôles,
       Règles : les trois questions, un seul écran. Il a REMPLACÉ les cinq
       onglets d'avant (rôles documentaires, rôles et droits, droits par
       section, aperçu des accès, casquettes). */
    { tab: 'acces', label: 'Rôles & accès' },
    /* Les familles d'actes d'exception (lot L3) : ce qu'on peut FAIRE de plus
       dans une rubrique ouverte — rouvrir, supprimer, décider. Attribuées par
       RÔLE depuis le 16/10/2026. */
    { tab: 'actes', label: 'Actes réservés' },
    /* Les douze droits du contrôle qualité, par TYPE de compte : le dernier
       réglage par profil, à verser aux rôles un jour. */
    { tab: 'droitscq', label: 'Droits du contrôle qualité' },
    /* ── Rapatriés du module Site le 30/08/2026 (RQ) ──────────────────────
       « Utilisateurs » et « Politique des comptes » se réglaient depuis la roue
       crantée du site. C'est le bon endroit ici : un compte donne accès au
       système qualité, et c'est la RQ qui répond de qui y entre. */
    { tab: 'utilisateurs', label: 'Utilisateurs' },
    { tab: 'politique', label: 'Politique des comptes' },
  ] },
  /* ── MISE À DISPOSITION DES MODULES (10/09/2026) ────────────────────────
     Sorti du groupe « Rôles & accès » le jour où sa garde est passée du rôle
     `rq` au PROFIL `superadmin`. Le laisser parmi les droits aurait laissé
     croire qu'un responsable qualité peut l'actionner — il ne peut pas, et
     c'est délibéré.
     ⚠️ CE N'EST PAS UN DROIT. Masquer une rubrique la retire à TOUT LE MONDE,
     super admin compris ; les droits restent intacts dessous, ce qui permet de
     rouvrir d'un seul geste sans se rappeler qui y avait accès. */
  { group: 'Mise à disposition des modules', icon: 'shield', role: 'superadmin', items: [
    { tab: 'deploiement', label: 'Modules mis à disposition' },
  ] },
  /* ── Points de règle (REG-3) ──────────────────────────────────────────────
     Les énoncés affichés derrière les « i », là où le choix se fait. Ils sont
     rangés au PARAMÉTRAGE et non dans un module à eux : ce ne sont pas des
     données du laboratoire, ce sont les règles selon lesquelles il travaille —
     au même titre que les rôles ou la codification.

     ⚠️ Réservé au `rq` pour la GESTION, alors que la LECTURE des mêmes règles
     est ouverte à tout le personnel (route `GET /ged/regles`, exemptée de
     section). Ce n'est pas une incohérence : une règle qu'on applique doit se
     lire ; la changer engage le système documentaire. */
  { group: 'Points de règle', icon: 'info', role: 'rq', items: [
    { tab: 'regles', label: 'Règles affichées aux écrans' },
  ] },
  { group: 'Mise en forme', icon: 'print', role: 'rq', items: [
    { tab: 'cartouche', label: 'Identité & cartouche' },
    { tab: 'office', label: 'Documents bureautiques' },
  ] },
  { group: 'Alertes', icon: 'mail', role: 'rq', items: [
    { tab: 'notif', label: 'Notifications & rappels' },
    { tab: 'imapnc', label: 'Relève e-mail (NC)' },
    // SEC-2 — les DEUX boîtes du laboratoire se règlent ici, côte à côte. Il en
    // existait une seconde configuration dans le manuel, que rien ne relevait :
    // deux formulaires pour un même compte, dont un décoratif.
    { tab: 'imapdemandes', label: 'Relève e-mail (réponses)' },
    { tab: 'matvalid', label: 'Validité des matériaux' },
  ] },
  // Le quiz de satisfaction s'affiche sur le SITE public mais se règle ICI :
  // les retours qu'il accompagne alimentent le registre « Retours des
  // usagers », dont la RQ est seule propriétaire — le réglage suit le registre,
  // pas l'écran qui l'affiche.
  { group: 'Retours des usagers', icon: 'mail', role: 'rq', items: [
    { tab: 'quiz', label: 'Quiz de satisfaction (site)' },
  ] },
  // P1 (26/08/2026) — le pop-up programmé : un mot, une photo, un mini-sondage,
  // affiché une fois à chaque personne à l'ouverture de l'application. Il se
  // règle ici parce qu'il s'ADRESSE au personnel — c'est une communication du
  // laboratoire, pas un réglage technique.
  { group: 'Communication', icon: 'send', role: 'rq', items: [
    { tab: 'annonces', label: 'Pop-up programmé (annonces)' },
  ] },
  /* Le CONTRÔLE INTERNE se règle ici, et non au bas de l'écran des cartes
     (RQ, 24/08/2026 : « les règles d'acceptation des CQI et la liaison au
     référentiel doivent être au niveau du paramétrage, et non à la suite de
     toute la liste de CQI »).

     Le catalogue des règles y était empilé SOUS les 126 cartes : pour changer
     une règle de Westgard, il fallait traverser la liste entière. Il ne parle
     d'aucune carte en particulier — il décrit ce que le MOTEUR sait évaluer.
     C'est un réglage du module, pas une ligne de registre.

     ⚠️ La « liaison au référentiel » a d'abord été rangée ici, puis RETIRÉE le
     même jour : elle portait l'écran de réconciliation, qui devinait le lien
     examen ↔ analyte par ressemblance. Ce lien est aujourd'hui une vraie
     relation, complète (330 analytes sur 330), et il se lit là où la question
     se pose — sur la fiche de l'examen et sur celle du paramètre. Un réglage
     n'avait pas à exister pour une devinette dont on n'a plus besoin. */
  { group: 'Contrôle interne', icon: 'chart', role: 'cqi', items: [
    { tab: 'cqiregles', label: 'Règles d’acceptation (Westgard)' },
  ] },
  // Lot N5 — le plan du laboratoire est un RÉFÉRENTIEL, au même titre que les
  // disciplines ou les types de documents : il se règle ici, et il est ensuite
  // consommé par les équipements, le stock et les copies maîtrisées.
  { group: 'Locaux', icon: 'map', role: 'rq', items: [
    /* Les lieux de stockage, sortis de Matériel le 10/10/2026, ont eu ici leur
       propre onglet trois jours : deux portes pour une seule réalité, et celle
       des lieux faisait taper zone, armoire et étagère en texte libre, sans
       rattacher la ligne au plan. Depuis le 13/10/2026 (Ines), le plan descend
       jusqu'à l'étagère et c'est là qu'on dit ce qui y est rangé. */
    { tab: 'emplacements', label: 'Plan du laboratoire & stockage' },
  ] },
  { group: 'Plannings & RH', icon: 'history', role: 'rh', items: [
    { tab: 'rhregles', label: 'Règles du temps de travail' },
    { tab: 'rhferies', label: 'Jours fériés' },
    { tab: 'rhregimes', label: 'Régimes spéciaux' },
  ] },
];

